// src/types/index.ts - Centralized type definitions

import { ReactNode } from 'react';

// =====================
// BASE TYPES
// =====================

export interface BaseEntity {
  id: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface BaseEntityWithDocumentId extends BaseEntity {
  documentId: string;
}

// =====================
// API RESPONSE TYPES
// =====================

export interface StrapiResponse<T> {
  data: T;
  meta: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface StrapiSingleResponse<T> {
  data: T;
}

export interface StrapiEntity {
  id: number;
  attributes: Record<string, unknown>;
}

export interface StrapiProviderMetadata {
  public_id?: string;
  resource_type?: string;
  [key: string]: unknown;
}

// =====================
// MEDIA TYPES
// =====================

export interface MediaFormat {
  url: string;
  width?: number;
  height?: number;
  size?: number;
  ext?: string;
  mime?: string;
}

export interface StrapiMedia extends BaseEntity {
  documentId: string;
  name: string;
  alternativeText?: string;
  caption?: string;
  width?: number;
  height?: number;
  formats?: {
    large?: MediaFormat;
    medium?: MediaFormat;
    small?: MediaFormat;
    thumbnail?: MediaFormat;
  } | null;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  provider_metadata?: StrapiProviderMetadata;
}

// =====================
// CONTENT TYPES
// =====================

// Organization Types
export type OrganizationType = 'University' | 'Government' | 'Industry Partner' | 'Research Institution' | 'Funding Agency';
export type PartnershipLevel = 'Primary' | 'Secondary' | 'Affiliated';

export interface Organization {
  id: number;
  attributes?: {
    name: string;
    description?: string;
    website?: string;
    organizationType: OrganizationType;
    partnershipLevel: PartnershipLevel;
    isActive: boolean;
    slug: string;
    createdAt: string;
    updatedAt: string;
    logo?: {
      data: StrapiMedia;
    };
    contactPerson?: {
      data: Person;
    };
  };
  // For direct access (when attributes are flattened)
  name?: string;
  description?: string;
  website?: string;
  organizationType?: OrganizationType;
  partnershipLevel?: PartnershipLevel;
  isActive?: boolean;
  slug?: string;
  logo?: StrapiMedia | { data: StrapiMedia };
  contactPerson?: Person | { data: Person };
}

// Person Types
export type PersonType = 'Lead Investigator' | 'Co-Lead' | 'Co-Investigator' | 'Current Student' | 'Program Committee' | 'Industry Advisory Board';
export type SpecialRole = 'Program Director' | 'Co-Director' | 'Chair' | 'Vice-Chair' | 'Lead Investigator' | 'Co-Lead Investigator';

export interface Person {
  id: number;
  attributes: {
    name: string;
    title: string;
    specialRole?: SpecialRole;
    email?: string;
    bio?: string;
    personType: PersonType;
    program?: string;
    projectTitle?: string;
    mandate?: string;
    linkedProfile?: string;
    testimonial?: string;
    slug: string;
    createdAt: string;
    updatedAt: string;
    photo?: {
      data: StrapiMedia;
    };
    affiliation?: {
      data: Organization;
    };
    researchAreas?: ResearchArea[];
  };
}

// Research Types
export interface ResearchArea {
  id: number;
  name: string;
  description?: string;
  theme?: 'T1' | 'T2' | 'T3' | 'T4' | 'T5' | 'T6';
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchTheme extends BaseEntityWithDocumentId {
  title: string;
  themeNumber: string; // "T1", "T2", etc.
  overview: string;
  objectives?: string;
  keyQuestions?: string;
  slug?: string;
  sortOrder?: number;
  relatedPartners?: Organization[];
  leadResearchers?: Person[];
  researchers?: Person[];
  relatedEvents?: Event[];
  relatedResources?: Resource[];
  featuredImage?: StrapiMedia;
}

// News Types
export type NewsCategory = 'News' | 'Announcement' | 'Media Coverage' | 'Newsletter';

export interface Tag {
  id: number;
  name: string;
  slug: string;
  color?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewsArticle extends BaseEntityWithDocumentId {
  title: string;
  slug?: string;
  excerpt?: string;
  content: string;
  publishedDate: string;
  category: NewsCategory;
  externalLink?: string;
  isFeatured: boolean;
  featuredImage?: StrapiMedia;
  author?: Person;
  tags?: Tag[];
}

// Event Types
export type EventType = 'Conference' | 'Seminar' | 'Workshop' | 'Networking' | 'Presentation' | 'Guest Lecture';
export type EventStatus = 'Upcoming' | 'Past' | 'Cancelled' | 'Postponed';

export interface Event extends BaseEntityWithDocumentId {
  title: string;
  description: string;
  startDateTime: string;
  endDateTime?: string;
  location: string;
  eventType: EventType;
  registrationLink?: string;
  registrationRequired: boolean;
  capacity?: number;
  isVirtual: boolean;
  virtualLink?: string;
  agenda?: string;
  slug?: string;
  eventStatus: EventStatus;
  relatedThemes: ResearchTheme[];
  speakers: Person[];
  featuredImage?: StrapiMedia | null;
}

// Resource Types
export type ResourceType = 
  | 'Document' 
  | 'Handbook' 
  | 'Application Form' 
  | 'Guide' 
  | 'Template'
  | 'Student Handbook'
  | 'Guidelines';

// Update the Resource interface to match your API data structure
export interface Resource extends BaseEntityWithDocumentId {
  title: string;
  description?: string;
  resourceType: ResourceType;
  externalLink?: string;
  category?: string;
  accessLevel?: string;
  version?: string;
  lastUpdated?: string;
  fileSize?: string;
  slug?: string;
  relatedThemes?: ResearchTheme[];
  file?: StrapiMedia | null;
}

// Publication Types
export interface Publication extends BaseEntityWithDocumentId {
  title: string;
  publicationYear: string;
  citation?: string;
  externalLink?: string;
  slug?: string;
  authors?: Person[];
  researchThemes?: ResearchTheme[];
}

// Site Settings Types
export type SocialPlatform = 'LinkedIn' | 'Twitter' | 'Facebook' | 'YouTube' | 'Instagram' | 'GitHub';

export interface SocialMediaLink {
  id: number;
  platform: SocialPlatform;
  url: string;
  displayText?: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteSettings extends BaseEntityWithDocumentId {
  siteName: string;
  siteDescription?: string;
  footerText?: string;
  copyrightText?: string;
  maintenanceMode: boolean;
  googleAnalyticsId?: string;
  primaryLogo?: StrapiMedia;
  favicon?: StrapiMedia;
  socialMediaDefaults?: SocialMediaLink[];
}

export interface ContactInformation {
  id: number;
  attributes: {
    generalEmail: string;
    phoneNumber?: string;
    address?: string;
    officeHours?: string;
    locationMap?: string;
    contactFormEnabled: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

// Homepage Types
export interface SEO {
  id: number;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  canonicalURL?: string;
  structuredData?: string;
}

export interface Homepage extends BaseEntityWithDocumentId {
  title: string;
  programDescription: string;
  introductoryVideoUrl?: string;
  welcomeMessage?: string;
  introductoryVideo?: StrapiMedia | null;
  heroImage?: StrapiMedia | null;
  featuredNews?: NewsArticle[];
  partners?: Organization[];
  seo?: SEO;
}

// =====================
// COMPONENT PROP TYPES
// =====================

// Navigation Types
export interface NavigationItem {
  label: string;
  href: string;
  dropdown?: NavigationSubItem[];
}

export interface NavigationSubItem {
  label: string;
  href: string;
}

export interface NavigationProps {
  activeRoute?: string;
}

// Component Props
export interface FloatingNewsWidgetProps {
  maxItems?: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // in minutes
  showWelcomeTooltip?: boolean;
}

export interface ResearchThemesSectionProps {
  showTitle?: boolean;
  maxThemes?: number;
  onThemeExplore?: (theme: ResearchTheme) => void;
}

export interface PartnerCarouselProps {
  autoPlaySpeed?: number;
  showControls?: boolean;
}

export interface VideoSectionProps {
  homepage?: Homepage;
  loading?: boolean;
}

export interface YouTubePlayerProps {
  videoId: string;
  title?: string;
  autoplay?: boolean;
}

export interface FooterProps {
  siteSettings?: SiteSettings;
  contactInfo?: ContactInformation;
}

// Styled Component Props (for shouldForwardProp)
export interface StyledComponentProps {
  isExpanded?: boolean;
  isMobile?: boolean;
  expanded?: boolean;
  isPlaying?: boolean;
  duration?: number;
  scrollWidth?: number;
  show?: boolean;
}

// =====================
// HOOK RETURN TYPES
// =====================

export interface UseHomepageReturn {
  homepage: Homepage | null;
  loading: boolean;
  error: string | null;
}

export interface UseApiDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =====================
// EVENT HANDLER TYPES
// =====================

export type ThemeExploreHandler = (theme: ResearchTheme) => void;
export type OrganizationClickHandler = (organization: Organization) => void;
export type NewsArticleClickHandler = (article: NewsArticle) => void;

// =====================
// API ERROR TYPES
// =====================

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public endpoint: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string, public endpoint: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface ApiResult<T> {
  data?: T;
  error?: string;
  status: number;
}

// =====================
// UTILITY TYPES
// =====================

export type Nullable<T> = T | null;
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// Generic component props
export interface BaseComponentProps {
  children?: ReactNode;
  className?: string;
}

// Grid system types for MUI v7
export interface GridSize {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export interface GridProps extends BaseComponentProps {
  size?: GridSize;
  container?: boolean;
  spacing?: number;
}

// Theme-related types
export interface ThemeColors {
  primary: string;
  secondary: string;
  dalGold: string;
  dalBlack: string;
}

// Form types (for future use)
export interface FormFieldProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  organization?: string;
}

// Search/Filter types
export interface SearchFilters {
  query?: string;
  category?: string;
  dateFrom?: string;
  dateTo?: string;
  tags?: string[];
}

export interface PaginationParams {
  page: number;
  pageSize: number;
  total?: number;
}

// =====================
// TYPE GUARDS
// =====================

export function isOrganization(obj: any): obj is Organization {
  return obj && typeof obj === 'object' && 'id' in obj && 
    (('attributes' in obj && 'name' in obj.attributes) || 'name' in obj);
}

export function isNewsArticle(obj: any): obj is NewsArticle {
  return obj && typeof obj === 'object' && 'id' in obj && 'title' in obj && 'category' in obj;
}

export function isResearchTheme(obj: any): obj is ResearchTheme {
  return obj && typeof obj === 'object' && 'id' in obj && 'themeNumber' in obj && 'title' in obj;
}

export function isStrapiMedia(obj: any): obj is StrapiMedia {
  return obj && typeof obj === 'object' && 'url' in obj && 'mime' in obj;
}